import { useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type RefObject } from "react";
import { ContextualPopover } from "./ContextualPopover";

const iso = (year: number, month: number, day: number) => `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

export function DocumentDatePicker({ anchorRef, value, label, shortcuts = [], focusOnOpen = false, onChoose, onClose }: {
  anchorRef: RefObject<HTMLElement | null>;
  value: string;
  label: string;
  shortcuts?: { label: string; value: string }[];
  focusOnOpen?: boolean;
  onChoose(value: string): void;
  onClose(): void;
}) {
  const initial = /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : new Date().toISOString().slice(0, 10);
  const [month, setMonth] = useState(() => new Date(`${initial}T12:00:00Z`).getUTCMonth());
  const [year, setYear] = useState(() => new Date(`${initial}T12:00:00Z`).getUTCFullYear());
  const [focusedDate, setFocusedDate] = useState(initial);
  const dayButtons = useRef(new Map<string, HTMLButtonElement>());
  const focusPending = useRef(focusOnOpen);
  useLayoutEffect(() => {
    if (!focusPending.current) return;
    dayButtons.current.get(focusedDate)?.focus();
    focusPending.current = false;
  }, [focusedDate, month, year]);
  const cells = useMemo(() => {
    const first = new Date(Date.UTC(year, month, 1)).getUTCDay();
    const count = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    return [...Array(first).fill(null), ...Array.from({ length: count }, (_, index) => index + 1)] as (number | null)[];
  }, [month, year]);
  const moveTo = (next: Date, focus: boolean) => {
    focusPending.current = focus;
    setMonth(next.getUTCMonth());
    setYear(next.getUTCFullYear());
    setFocusedDate(iso(next.getUTCFullYear(), next.getUTCMonth(), next.getUTCDate()));
  };
  const shift = (delta: number, focus = false) => {
    const day = new Date(`${focusedDate}T12:00:00Z`).getUTCDate();
    const lastDay = new Date(Date.UTC(year, month + delta + 1, 0)).getUTCDate();
    moveTo(new Date(Date.UTC(year, month + delta, Math.min(day, lastDay))), focus);
  };
  const navigate = (event: KeyboardEvent<HTMLButtonElement>, day: number) => {
    const current = new Date(Date.UTC(year, month, day));
    const offsets: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7, Home: -current.getUTCDay(), End: 6 - current.getUTCDay() };
    const offset = offsets[event.key];
    if (offset !== undefined) {
      event.preventDefault();
      current.setUTCDate(day + offset);
      moveTo(current, true);
    } else if (event.key === "PageUp" || event.key === "PageDown") {
      event.preventDefault();
      shift(event.key === "PageUp" ? -1 : 1, true);
    }
  };
  const choose = (next: string) => { onChoose(next); onClose(); anchorRef.current?.focus(); };
  const today = new Date().toISOString().slice(0, 10);
  return <ContextualPopover anchorRef={anchorRef} label={label} onClose={onClose} width={316} anchorIndicator><div className="document-date-picker">
    {shortcuts.length ? <div className="document-date-shortcuts">{shortcuts.map(shortcut => <button key={shortcut.label} type="button" aria-pressed={shortcut.value === value} onClick={() => choose(shortcut.value)}>{shortcut.label}</button>)}</div> : null}
    <div className="document-date-picker-heading"><button type="button" aria-label="Previous month" onClick={() => shift(-1)}>‹</button><strong>{new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(Date.UTC(year, month, 1)))}</strong><button type="button" aria-label="Next month" onClick={() => shift(1)}>›</button></div>
    <div className="document-date-picker-grid">{["S", "M", "T", "W", "T", "F", "S"].map((day, index) => <span key={index} aria-hidden="true">{day}</span>)}{cells.map((day, index) => day === null ? <span key={`blank-${index}`}/> : <button key={day} type="button" ref={button => { const date = iso(year, month, day); if (button) dayButtons.current.set(date, button); else dayButtons.current.delete(date); }} tabIndex={iso(year, month, day) === focusedDate ? 0 : -1} className={`${iso(year, month, day) === value ? "is-selected" : ""}${iso(year, month, day) === today ? " is-today" : ""}`} aria-label={new Intl.DateTimeFormat("en-US", { dateStyle: "long", timeZone: "UTC" }).format(new Date(Date.UTC(year, month, day)))} aria-pressed={iso(year, month, day) === value} onFocus={() => setFocusedDate(iso(year, month, day))} onKeyDown={event => navigate(event, day)} onClick={() => choose(iso(year, month, day))}>{day}</button>)}</div>
  </div></ContextualPopover>;
}
